import { computed, effect, signal } from '@preact/signals';
import { createField, type GridSpec, type ScalarField } from '../sim/grid.ts';
import {
  buildTerrain,
  resampleToTerrain,
  scenarioInput,
  type Conditions,
  type SnowpackState,
  type TerrainState,
} from '../sim/scene.ts';
import type { ScenarioRequest, ScenarioResponse } from '../sim/scenario.worker.ts';
import type { Grain } from '../sim/snowpack.ts';
import type { AvalancheProblem } from '../sim/stability.ts';
import {
  DEFAULT_PRESET,
  fair as presetFair,
  storm as presetStorm,
  type ScenarioPreset,
} from '../sim/presets.ts';
import {
  eventAt,
  snapshotSkier,
  type TimelineEvent,
  type TimelineSnapshot,
} from '../sim/timeline.ts';
import { DEFAULT_WEAK_LAYERS } from '../sim/weaklayers.ts';
import {
  autoResolution,
  estimateMemory,
  planGrids,
  type AreaSelection,
  type Resolution,
} from '../sim/resolution.ts';
import { DEFAULT_INITIAL_SNOW } from '../sim/snowcover.ts';
import { DEFAULT_TEMPERATURE } from '../sim/temperature.ts';
import { fetchCrownCover } from '../data/sr16.ts';
import { maskBelow, nearestResampleCategorical } from '../render/overlayColors.ts';
import { gridNorthBearing } from '../terrain/projections.ts';
import { fetchElevation } from '../terrain/wcs.ts';

/**
 * App state. Plain signals; UI components and the renderer subscribe to what they need.
 * The selection and conditions are mirrored into the URL hash so a scenario can be shared.
 */

export type OverlayMode =
  | 'natural'
  | 'snowDepth'
  | 'slope'
  | 'temperature'
  | 'precipPhase'
  | 'stormSnow'
  | 'windLoading'
  | 'windSpeed'
  | 'shelter'
  | 'stability'
  | 'naturalStability'
  | 'problem'
  | 'criticalDepth'
  | 'propagation'
  | 'forest'
  | 'avDeposit'
  | 'avNet'
  | 'avOrigin'
  | 'avMaxSpeed'
  | 'avMaxPressure'
  | 'avMaxThickness';
export type View = 'map' | 'scene';

/** Overlay modes fed by avalanche run data rather than the scenario snowpack (see
 * `state/avalanche.ts`'s `avalancheOverlayField`). */
export const AVALANCHE_OVERLAY_MODES: ReadonlySet<OverlayMode> = new Set([
  'avDeposit',
  'avNet',
  'avOrigin',
  'avMaxSpeed',
  'avMaxPressure',
  'avMaxThickness',
]);

/** Overlays offered in simple mode's View section. */
export const SIMPLE_OVERLAY_MODES: ReadonlySet<OverlayMode> = new Set([
  'natural',
  'snowDepth',
  'windLoading',
  'stability',
  'problem',
  'avNet',
]);

export type LoadStatus =
  | { kind: 'idle' }
  | { kind: 'loading'; done: number; total: number }
  | { kind: 'error'; message: string };

export const MIN_AREA_SIZE_M = 250;
export const MAX_AREA_SIZE_M = 10_000;

export const view = signal<View>('map');

/**
 * Simple mode shows the essentials (conditions in plain terms, click to release, a short
 * result); advanced shows every parameter and statistic. Remembered per browser.
 */
export type UiMode = 'simple' | 'advanced';
const UI_MODE_KEY = 'snow-sim.uiMode';
function loadUiMode(): UiMode {
  try {
    return localStorage.getItem(UI_MODE_KEY) === 'advanced' ? 'advanced' : 'simple';
  } catch {
    return 'simple';
  }
}
export const uiMode = signal<UiMode>(loadUiMode());
effect(() => {
  const mode = uiMode.value;
  try {
    localStorage.setItem(UI_MODE_KEY, mode);
  } catch {
    // Storage unavailable (private window, Node tests): the mode just isn't remembered.
  }
});
export const area = signal<AreaSelection | null>(null);
/** null = automatic from area + budget. */
export const resolutionOverride = signal<Resolution | null>(null);
export const conditions = signal<Conditions>(presetConditions(DEFAULT_PRESET));

/** Conditions for a preset, keeping the current existing-snow settings. */
export function presetConditions(
  preset: ScenarioPreset,
  base: Pick<Conditions, 'temperature' | 'initialSnow'> = {
    temperature: DEFAULT_TEMPERATURE,
    initialSnow: DEFAULT_INITIAL_SNOW,
  },
): Conditions {
  return {
    temperature: preset.events[0]?.temperature ?? base.temperature,
    initialSnow: base.initialSnow,
    weakLayers: preset.weakLayers,
    events: preset.events,
    evaluationHour: preset.evaluationHour,
  };
}

export function applyPreset(preset: ScenarioPreset): void {
  conditions.value = presetConditions(preset, conditions.peek());
}

/**
 * While scrubbing the timeline, the hour being shown from snapshots (instant, approximate);
 * null when showing the exact evaluated state. Releasing the scrubber sets
 * `conditions.evaluationHour`, which computes the exact state there.
 */
export const viewHour = signal<number | null>(null);

/** The snapshot nearest to (at or before) the viewed hour, while scrubbing. */
export const viewSnapshot = computed<TimelineSnapshot | null>(() => {
  const h = viewHour.value;
  const sp = snowpack.value;
  if (h === null || !sp) return null;
  let best: TimelineSnapshot | null = null;
  for (const s of sp.timeline.snapshots) {
    if (s.hour <= h + 1e-6) best = s;
    else break;
  }
  return best ?? sp.timeline.snapshots[0] ?? null;
});

/** The hour on screen: the scrubbed hour while scrubbing, else the evaluation hour. */
export const displayHour = computed(() => viewHour.value ?? conditions.value.evaluationHour);

/** The weather event at the hour on screen (null for an empty timeline). */
export const displayEvent = computed<TimelineEvent | null>(
  () => eventAt(conditions.value.events, displayHour.value)?.event ?? null,
);

/**
 * Conditions for drawing: `temperature` is the displayed event's, so the temperature and
 * precipitation-phase overlays match the hour on screen (`conditions.temperature` is only the
 * default for new events).
 */
export const displayConditions = computed<Conditions>(() => {
  const c = conditions.value;
  const e = displayEvent.value;
  return e ? { ...c, temperature: e.temperature } : c;
});

export const overlay = signal<OverlayMode>('natural');
export const loadStatus = signal<LoadStatus>({ kind: 'idle' });
export const terrain = signal<TerrainState | null>(null);

/**
 * Crown cover (0–1) on the terrain grid, from NIBIO SR16 — loaded after the terrain, and
 * optional: without it (outside Norway's forest map, or the WMS is down) avalanches just ignore
 * forest.
 */
export const forestCover = signal<ScalarField | null>(null);
export type ForestStatus = 'idle' | 'loading' | 'ready' | 'unavailable';
export const forestStatus = signal<ForestStatus>('idle');
let forestAbort: AbortController | null = null;

effect(() => {
  const t = terrain.value;
  forestAbort?.abort();
  forestCover.value = null;
  if (!t) {
    forestStatus.value = 'idle';
    return;
  }
  const ac = new AbortController();
  forestAbort = ac;
  forestStatus.value = 'loading';
  const spec = t.plan.terrain;
  fetchCrownCover(spec, ac.signal)
    .then((cover) => {
      if (ac.signal.aborted) return;
      forestCover.value = { spec, data: cover };
      forestStatus.value = 'ready';
    })
    .catch(() => {
      if (!ac.signal.aborted) forestStatus.value = 'unavailable';
    });
});

/** Width/height (m) for the area about to be picked, before a centre has been clicked. Kept in
 * sync with `area`'s size once an area exists, so the sliders have a sensible value either way. */
export const pendingAreaSize = signal<{ width: number; height: number }>({
  width: 2000,
  height: 2000,
});

/** Vertical exaggeration applied to the 3D terrain mesh (1 = true scale). */
export const verticalExaggeration = signal<number>(1);

/** Show instanced wind-direction arrows over the terrain (storm must be enabled to see any). */
export const showWindArrows = signal<boolean>(false);

export interface HoverProbe {
  xUtm: number;
  yUtm: number;
  /** m */
  elevation: number;
  /** deg */
  slope: number;
  /** deg clockwise from north; NaN when flat */
  aspect: number;
  /** m, total snow depth (existing + storm) */
  snowDepth: number;
  /** °C */
  airTemp: number;
  /** 0…1, fraction of precipitation falling as snow at this point */
  snowFraction: number;
  /** Number of snow layers in the column under the cursor. */
  layerCount?: number;
  /** Grain class of the top layer. */
  topGrain?: Grain;
  /** m, storm snowfall (before wind redistribution). Present only when a storm result exists. */
  stormSnowfall?: number;
  /** m, net wind change: + loaded / − scoured. Present only when a storm result exists. */
  windChange?: number;
  /** m/s. Present only when a storm result exists. */
  windSpeed?: number;
  /** True bearing (deg) the wind blows from. Present only when a storm result exists. */
  windFromTrueDeg?: number;
  /** Winstral Sx (deg): + sheltered, − exposed. Present only when a storm result exists. */
  sx?: number;
  /** mm. Present only when a storm result exists. */
  rain?: number;
  /** SK38 skier stability index of the critical layer (capped at 9.99). */
  skier?: number;
  /** Avalanche problem at this point, if the slope and stability warrant one. */
  problem?: AvalancheProblem;
  /** Grain of the critical (weakest) layer, if there is a slab over one. */
  criticalGrain?: Grain;
  /** Depth (m, vertical) of the critical layer's top below the surface. */
  criticalDepth?: number;
  /** m, cumulative avalanche debris depth at this point (all runs). */
  avDebrisDepth?: number;
  /** m, cumulative snow removed at this point (release + entrainment, all runs). */
  avRemovedDepth?: number;
  /** m, cumulative net snow-depth change at this point (all runs). */
  avNetChange?: number;
  /** %, share of the selected run's deposit here that came from entrainment. */
  avOriginSharePct?: number;
  /** m/s, the selected run's max speed reached at this point. */
  avMaxSpeed?: number;
  /** kPa, the selected run's max impact pressure at this point. */
  avMaxPressureKPa?: number;
}

/** Point currently under the pointer in the 3D scene, or null when not hovering the terrain. */
export const hoverProbe = signal<HoverProbe | null>(null);

/** Snowpack cell selected by clicking the terrain (pointer down + up without dragging), for the
 * snow-profile panel. Index into the snowpack grid; null when no cell is selected. Reset whenever
 * the terrain changes, since cell indices from a previous area are meaningless. */
export const selectedCell = signal<number | null>(null);

/** Event selected in the timeline editor (advanced mode); the timeline bar highlights it and
 * clicking one of its blocks sets it, so the two stay in sync despite living in different parts
 * of the layout (the 3D view vs. the side panel). Null falls back to the first event. */
export const selectedTimelineEventId = signal<string | null>(null);

/** Move the selection to a new centre, keeping its current size (or the pending size, if none
 * is selected yet). */
export function setAreaCenter(centerX: number, centerY: number): void {
  const size = area.value ?? pendingAreaSize.value;
  area.value = { centerX, centerY, width: size.width, height: size.height };
}

/** Resize the selection (or the pending size, if none is selected yet), clamped to the allowed
 * range. */
export function setAreaSize(width: number, height: number): void {
  const w = clampAreaSize(width);
  const h = clampAreaSize(height);
  pendingAreaSize.value = { width: w, height: h };
  if (area.value) area.value = { ...area.value, width: w, height: h };
}

function clampAreaSize(v: number): number {
  if (!Number.isFinite(v)) return MIN_AREA_SIZE_M;
  return Math.min(MAX_AREA_SIZE_M, Math.max(MIN_AREA_SIZE_M, v));
}

export const resolution = computed<Resolution | null>(() => {
  const a = area.value;
  if (!a) return null;
  return resolutionOverride.value ?? autoResolution(a.width * a.height);
});

export const gridPlan = computed(() => {
  const a = area.value;
  const r = resolution.value;
  return a && r ? planGrids(a, r) : null;
});

export const memoryEstimate = computed(() =>
  gridPlan.value ? estimateMemory(gridPlan.value) : null,
);

// ---- Snowpack scenario (runs in a worker) -------------------------------------------------

export type ScenarioStatus =
  | { kind: 'idle'; ms: number | null }
  | { kind: 'running'; fraction: number }
  | { kind: 'error'; message: string };

/** Latest finished snowpack for the current terrain + conditions (null while none is ready). */
export const snowpack = signal<SnowpackState | null>(null);
export const scenarioStatus = signal<ScenarioStatus>({ kind: 'idle', ms: null });

/**
 * Total snow depth (m) resampled onto the terrain grid, for the mesh surface and the "natural"
 * family of overlays. Bare (zero-snow) terrain while no snowpack result exists yet, so the scene
 * always has something sane to draw. Computed once here rather than in both the renderer effect
 * and the legend.
 */
export const snowDepthOnTerrain = computed<ScalarField | null>(() => {
  const t = terrain.value;
  if (!t) return null;
  const base = snowDepthBase.value;
  if (!base || base.spec !== t.plan.terrain) return createField(t.plan.terrain);
  const delta = avalancheDepthDelta.value;
  if (!delta) return base;
  // Avalanches are kept at terrain resolution on top of the scenario's snow depth, so debris
  // cones and scars show at full detail rather than blurred to the snowpack grid.
  const out = createField(t.plan.terrain);
  for (let i = 0; i < out.data.length; i++) {
    out.data[i] = Math.max(0, base.data[i]! + delta[i]!);
  }
  return out;
});

/**
 * Overlays a timeline snapshot can show while scrubbing. The others need the full state at an
 * hour, which is computed when the scrubber is released; until then they show nothing.
 */
export const SNAPSHOT_OVERLAY_MODES: ReadonlySet<OverlayMode> = new Set<OverlayMode>([
  'natural',
  'snowDepth',
  'slope',
  'temperature',
  'precipPhase',
  'stability',
  'problem',
  'forest',
]);

/**
 * Snow depth (m) to draw: the snapshot's while scrubbing, otherwise the exact
 * `snowDepthOnTerrain`. Only for display — avalanches always run on the exact state.
 */
export const displaySnowDepth = computed<ScalarField | null>(() => {
  const snap = viewSnapshot.value;
  const t = terrain.value;
  const sp = snowpack.value;
  if (!snap || !t || !sp) return snowDepthOnTerrain.value;
  const depth = createField(sp.spec);
  for (let i = 0; i < depth.data.length; i++) depth.data[i] = snap.depthCm[i]! / 100;
  return resampleToTerrain(depth, t.plan.terrain);
});

/**
 * Scenario snow depth on the terrain grid, captured when a scenario result arrives — before any
 * avalanche mutates the snowpack.
 */
export const snowDepthBase = signal<ScalarField | null>(null);

/**
 * Bumped whenever the snowpack is changed in place (an avalanche applied or undone), so anything
 * derived from it recomputes even though the SnowpackState object is the same.
 */
export const snowpackVersion = signal(0);

/** Cumulative snow-depth change from avalanches (m) on the terrain grid, or null if none. */
export const avalancheDepthDelta = signal<Float32Array | null>(null);

/** Slabs thinner than this (m) don't count as a slab for the stability overlays' neutral mask —
 * a little looser than STABILITY_MODEL.minSlab so bilinear resampling near a slab's edge doesn't
 * flicker between a class colour and neutral. */
const STABILITY_OVERLAY_MIN_SLAB_M = 0.05;

function activeSnowpackField(
  mode: OverlayMode,
  sp: SnowpackState | null,
  terrainSpec: GridSpec,
): ScalarField | null {
  if (!sp) return null;
  const storm = sp.storm;
  switch (mode) {
    case 'stormSnow': {
      if (!storm) return null;
      const net = createField(storm.snowfall.spec);
      for (let i = 0; i < net.data.length; i++) {
        net.data[i] = storm.snowfall.data[i]! + storm.windChange.data[i]!;
      }
      return resampleToTerrain(net, terrainSpec);
    }
    case 'windLoading':
      return storm ? resampleToTerrain(storm.windChange, terrainSpec) : null;
    case 'windSpeed':
      return storm?.wind ? resampleToTerrain(storm.wind.speed, terrainSpec) : null;
    case 'shelter':
      return storm?.wind ? resampleToTerrain(storm.wind.sx, terrainSpec) : null;
    case 'stability':
    case 'naturalStability': {
      const value = resampleToTerrain(
        mode === 'stability' ? sp.stability.skier : sp.stability.natural,
        terrainSpec,
      );
      const slab = resampleToTerrain(sp.stability.slabThickness, terrainSpec);
      return maskBelow(value, slab, STABILITY_OVERLAY_MIN_SLAB_M);
    }
    case 'criticalDepth':
    case 'propagation': {
      const field = resampleToTerrain(
        mode === 'criticalDepth' ? sp.stability.criticalDepth : sp.stability.propagation,
        terrainSpec,
      );
      return maskBelow(field, field, 1e-6);
    }
    case 'problem':
      return nearestResampleCategorical(sp.stability.problem, sp.spec, terrainSpec);
    default:
      return null;
  }
}

/**
 * The field backing whichever storm/stability overlay is active, resampled onto the terrain grid
 * — null for the other overlays, and for these ones while no (matching) result exists yet.
 * Resampling only the active overlay (rather than all of them every time) keeps this cheap.
 */
export const activeOverlayField = computed<ScalarField | null>(() => {
  const t = terrain.value;
  if (!t) return null;
  if (overlay.value === 'forest') {
    const f = forestCover.value;
    return f && f.spec === t.plan.terrain ? f : null;
  }
  const snap = viewSnapshot.value;
  const sp = snowpack.value;
  if (snap && sp) return snapshotField(overlay.value, snap, sp.spec, t.plan.terrain);
  void snowpackVersion.value; // recompute after in-place avalanche changes
  return activeSnowpackField(overlay.value, sp, t.plan.terrain);
});

function snapshotField(
  mode: OverlayMode,
  snap: TimelineSnapshot,
  snowSpec: GridSpec,
  terrainSpec: GridSpec,
): ScalarField | null {
  if (mode === 'problem') return nearestResampleCategorical(snap.problem, snowSpec, terrainSpec);
  if (mode !== 'stability') return null;
  const skier = createField(snowSpec);
  const slab = createField(snowSpec);
  for (let i = 0; i < skier.data.length; i++) {
    const v = snapshotSkier(snap.skier[i]!);
    skier.data[i] = Number.isNaN(v) ? 0 : v;
    slab.data[i] = Number.isNaN(v) ? 0 : 1;
  }
  return maskBelow(
    resampleToTerrain(skier, terrainSpec),
    resampleToTerrain(slab, terrainSpec),
    0.5,
  );
}

const SCENARIO_DEBOUNCE_MS = 120;
let worker: Worker | null = null;
let nextId = 0;
let runningId: number | null = null;
let pending: { terrain: TerrainState; conditions: Conditions } | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function scenarioWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('../sim/scenario.worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (e: MessageEvent<ScenarioResponse>) => {
    const msg = e.data;
    if (msg.id !== runningId) return;
    if (msg.kind === 'progress') {
      scenarioStatus.value = { kind: 'running', fraction: msg.fraction };
      return;
    }
    runningId = null;
    if (msg.kind === 'done') {
      const t = terrain.peek();
      // Copy: resampleToTerrain returns the field itself when the grids coincide, and avalanches
      // later change snowpack.depth in place.
      const depth = t ? resampleToTerrain(msg.state.depth, t.plan.terrain) : null;
      snowDepthBase.value = depth
        ? { spec: depth.spec, data: Float32Array.from(depth.data) }
        : null;
      avalancheDepthDelta.value = null;
      let state = msg.state;
      if (runningSnapshotKey !== null) {
        lastSnapshotKey = runningSnapshotKey;
      } else {
        // Evaluation-hour-only run: carry over the snapshots from the last full run.
        const prev = snowpack.peek();
        if (prev)
          state = { ...state, timeline: { ...state.timeline, snapshots: prev.timeline.snapshots } };
      }
      snowpack.value = state;
      // The exact state for the hour being viewed has arrived: stop showing snapshots.
      if (viewHour.peek() === state.evaluationHour) viewHour.value = null;
      scenarioStatus.value = { kind: 'idle', ms: msg.ms };
    } else {
      scenarioStatus.value = { kind: 'error', message: msg.message };
    }
    dispatchPending();
  };
  worker.onerror = (e) => {
    runningId = null;
    scenarioStatus.value = { kind: 'error', message: e.message || 'Simulation worker failed' };
  };
  return worker;
}

/** Send the newest pending request, unless one is already running (it'll follow when done). */
function dispatchPending(): void {
  if (runningId !== null || !pending) return;
  const { terrain: t, conditions: c } = pending;
  pending = null;
  // Results for an outdated terrain are useless; skip if the terrain has since changed.
  if (t !== terrain.peek()) return;
  const id = ++nextId;
  runningId = id;
  scenarioStatus.value = { kind: 'running', fraction: 0 };
  // Only the evaluation hour moved (scrubbing): stop the run there and keep the snapshots we
  // already have — they don't depend on where we evaluate.
  const key = timelineKey(t, c);
  const onlyHourChanged = key === lastSnapshotKey && snowpack.peek() !== null;
  runningSnapshotKey = onlyHourChanged ? null : key;
  const request: ScenarioRequest = {
    id,
    input: { ...scenarioInput(t, c), recordSnapshots: !onlyHourChanged },
  };
  scenarioWorker().postMessage(request);
}

/** Identity of everything except the evaluation hour. */
function timelineKey(t: TerrainState, c: Conditions): string {
  const { evaluationHour, ...rest } = c;
  void evaluationHour;
  return `${t.plan.terrain.minX},${t.plan.terrain.maxY},${t.plan.terrain.cellSize}:${JSON.stringify(rest)}`;
}
let lastSnapshotKey: string | null = null;
let runningSnapshotKey: string | null = null;

let snowpackTerrain: TerrainState | null = null;

effect(() => {
  const t = terrain.value;
  const c = conditions.value;
  if (t !== snowpackTerrain) {
    // A result for another area must never be drawn on this one.
    snowpackTerrain = t;
    snowpack.value = null;
    selectedCell.value = null;
  }
  if (!t) {
    pending = null;
    return;
  }
  pending = { terrain: t, conditions: c };
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(dispatchPending, SCENARIO_DEBOUNCE_MS);
});

let inflight: AbortController | null = null;

export async function loadTerrain(): Promise<void> {
  const plan = gridPlan.value;
  if (!plan) return;
  inflight?.abort();
  const ctrl = new AbortController();
  inflight = ctrl;
  loadStatus.value = { kind: 'loading', done: 0, total: 1 };
  try {
    const elevation = await fetchElevation(plan.terrain, {
      signal: ctrl.signal,
      onProgress: (p) => (loadStatus.value = { kind: 'loading', ...p }),
    });
    if (ctrl.signal.aborted) return;
    const cx = plan.terrain.minX + (plan.terrain.cols * plan.terrain.cellSize) / 2;
    const cy = plan.terrain.maxY - (plan.terrain.rows * plan.terrain.cellSize) / 2;
    terrain.value = buildTerrain(plan, elevation, gridNorthBearing(cx, cy));
    loadStatus.value = { kind: 'idle' };
    view.value = 'scene';
  } catch (err) {
    if (ctrl.signal.aborted) return;
    loadStatus.value = { kind: 'error', message: err instanceof Error ? err.message : String(err) };
  } finally {
    if (inflight === ctrl) inflight = null;
  }
}

export function cancelLoad(): void {
  inflight?.abort();
  inflight = null;
  loadStatus.value = { kind: 'idle' };
}

// ---- URL hash persistence ---------------------------------------------------------------

interface HashState {
  area?: AreaSelection;
  res?: Resolution;
  cond?: Conditions;
}

/**
 * Conditions from a (possibly older) shared link. Links from before the timeline had one `storm`
 * plus `hoursAfterStorm`: they become a storm event followed by fair weather.
 */
function restoreConditions(c: Partial<Conditions> & LegacyConditions): Conditions {
  const temperature = { ...DEFAULT_TEMPERATURE, ...c.temperature };
  let events = Array.isArray(c.events) ? c.events : null;
  let evaluationHour = Number.isFinite(c.evaluationHour) ? c.evaluationHour! : null;
  if (!events && c.storm) {
    const st = c.storm;
    const after = Number.isFinite(c.hoursAfterStorm) ? Math.max(0, c.hoursAfterStorm!) : 0;
    events = [];
    if (st.enabled !== false) {
      events.push({
        ...presetStorm(
          st.durationHours ?? 24,
          st.newSnowCm ?? 30,
          st.windSpeed ?? 12,
          st.windFromDeg ?? 270,
          temperature,
        ),
        referenceElevation: st.referenceElevation ?? 500,
        orographicPctPer100m: st.orographicPctPer100m ?? 5,
        inflow: st.inflow ?? 'equilibrium',
      });
    }
    events.push(presetFair(Math.max(24, after), false, temperature));
    evaluationHour = (st.enabled !== false ? Math.round(st.durationHours ?? 24) : 0) + after;
  }
  return {
    temperature,
    initialSnow: { ...DEFAULT_INITIAL_SNOW, ...c.initialSnow },
    weakLayers: Array.isArray(c.weakLayers)
      ? c.weakLayers.map((w) => ({ ...DEFAULT_WEAK_LAYERS[0]!, ...w }))
      : DEFAULT_PRESET.weakLayers,
    events: events ?? DEFAULT_PRESET.events,
    evaluationHour: evaluationHour ?? DEFAULT_PRESET.evaluationHour,
    ...(events && typeof c.startTime === 'string' && !Number.isNaN(Date.parse(c.startTime))
      ? { startTime: c.startTime }
      : {}),
  };
}

/** Fields of the pre-timeline conditions, for restoring old links. */
interface LegacyConditions {
  storm?: {
    enabled?: boolean;
    durationHours?: number;
    newSnowCm?: number;
    referenceElevation?: number;
    orographicPctPer100m?: number;
    windSpeed?: number;
    windFromDeg?: number;
    inflow?: 'equilibrium' | 'none';
  };
  hoursAfterStorm?: number;
}

export function readHash(): void {
  const raw = location.hash.slice(1);
  if (!raw) return;
  try {
    const s = JSON.parse(decodeURIComponent(raw)) as HashState;
    if (s.area) {
      area.value = s.area;
      pendingAreaSize.value = { width: s.area.width, height: s.area.height };
    }
    if (s.res) resolutionOverride.value = s.res;
    if (s.cond) conditions.value = restoreConditions(s.cond);
  } catch {
    // Ignore malformed hashes; the app starts from defaults.
  }
}

export function persistToHash(): () => void {
  return effect(() => {
    const s: HashState = { cond: conditions.value };
    if (area.value) s.area = area.value;
    if (resolutionOverride.value) s.res = resolutionOverride.value;
    history.replaceState(null, '', `#${encodeURIComponent(JSON.stringify(s))}`);
  });
}
